import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const TrainerSetup = ({ onBackToHome, onSetupComplete, userData }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: userData?.email || localStorage.getItem('email') || '',
    phone_number: '',
    address: '',
    business_name: '',
    time_zone: '',
    business_street: '',
    business_apt: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    business_address: '',
    credentials: [],
    training_philosophy: [],
    areas_of_expertise: [],
    experience_years: '',
    year_began_training: '',
    first_10_min_rate: '',
    per_minute_rate: '',
    bio: '',
    legal_states: [],
    formal_degree: '',
    professional_school: '',
    other_credentials: '',
    other_sport_judge: '',
    breed_expert: '',
    ccf_affiliation: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('intro');
  const [completedTabs, setCompletedTabs] = useState(new Set());
  const [credentialOptions, setCredentialOptions] = useState([]);
  const [trainingPhilosophyOptions, setTrainingPhilosophyOptions] = useState([]);
  const [areasOfExpertiseOptions, setAreasOfExpertiseOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [uploadedDocuments, setUploadedDocuments] = useState({
    resume: null,
    certificates: null,
    degrees: null,
    insurance: null
  });
  const [pendingFiles, setPendingFiles] = useState({});
  const [backgroundForm, setBackgroundForm] = useState({
    legal_name: '',
    date_of_birth: '',
    consent: false
  });
  const [backgroundSubmitting, setBackgroundSubmitting] = useState(false);
  const [backgroundError, setBackgroundError] = useState('');
  const [backgroundSuccess, setBackgroundSuccess] = useState(false);

  // Fetch options from backend on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        const [credentialsRes, philosophyRes, expertiseRes] = await Promise.all([
          apiService.getCredentialsOptions(),
          apiService.getTrainingPhilosophyOptions(),
          apiService.getAreasOfExpertiseOptions()
        ]);

        if (credentialsRes.Code === 200) {
          setCredentialOptions(credentialsRes.options || []);
        }
        if (philosophyRes.Code === 200) {
          setTrainingPhilosophyOptions(philosophyRes.options || []);
        }
        if (expertiseRes.Code === 200) {
          setAreasOfExpertiseOptions(expertiseRes.options || []);
        }
      } catch (err) {
        console.error('Error fetching options:', err);
        setError('Failed to load options. Please refresh the page.');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  // Update email when userData changes
  useEffect(() => {
    if (userData?.email) {
      setFormData(prev => ({
        ...prev,
        email: userData.email
      }));
    }
  }, [userData]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'background') {
      setBackgroundForm(prev => ({
        ...prev,
        legal_name: prev.legal_name || `${formData.first_name || ''} ${formData.last_name || ''}`.trim()
      }));
    }
  }, [activeTab, formData.first_name, formData.last_name]);

  // Handle payment success callback from Stripe Payment Link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trainerId = urlParams.get('trainer_id');
    const paymentSuccess = urlParams.get('payment') === 'success';

    if (trainerId && paymentSuccess) {
      const verifyPayment = async () => {
        try {
          const response = await apiService.verifyTrainerRegistrationPayment({
            trainer_id: trainerId
          });
          
          if (response.Code === 200 && response.payment_status === 'completed') {
            // Payment verified, redirect to completion
            setTimeout(() => {
              onSetupComplete();
            }, 2000);
          } else {
            setError('Payment verification failed. Please contact support.');
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          setError('Failed to verify payment. Please contact support.');
        }
      };
      
      verifyPayment();
    }
  }, [onSetupComplete]);

  // Time zone options
  const timeZoneOptions = [
    'Pacific Time (PT)',
    'Mountain Time (MT)',
    'Central Time (CT)',
    'Eastern Time (ET)',
    'Alaska Time (AKT)',
    'Hawaii Time (HST)'
  ];

  // US States options
  const stateOptions = [
    { value: 'AL', label: 'Alabama (AL)' },
    { value: 'AK', label: 'Alaska (AK)' },
    { value: 'AZ', label: 'Arizona (AZ)' },
    { value: 'AR', label: 'Arkansas (AR)' },
    { value: 'CA', label: 'California (CA)' },
    { value: 'CO', label: 'Colorado (CO)' },
    { value: 'CT', label: 'Connecticut (CT)' },
    { value: 'DE', label: 'Delaware (DE)' },
    { value: 'FL', label: 'Florida (FL)' },
    { value: 'GA', label: 'Georgia (GA)' },
    { value: 'HI', label: 'Hawaii (HI)' },
    { value: 'ID', label: 'Idaho (ID)' },
    { value: 'IL', label: 'Illinois (IL)' },
    { value: 'IN', label: 'Indiana (IN)' },
    { value: 'IA', label: 'Iowa (IA)' },
    { value: 'KS', label: 'Kansas (KS)' },
    { value: 'KY', label: 'Kentucky (KY)' },
    { value: 'LA', label: 'Louisiana (LA)' },
    { value: 'ME', label: 'Maine (ME)' },
    { value: 'MD', label: 'Maryland (MD)' },
    { value: 'MA', label: 'Massachusetts (MA)' },
    { value: 'MI', label: 'Michigan (MI)' },
    { value: 'MN', label: 'Minnesota (MN)' },
    { value: 'MS', label: 'Mississippi (MS)' },
    { value: 'MO', label: 'Missouri (MO)' },
    { value: 'MT', label: 'Montana (MT)' },
    { value: 'NE', label: 'Nebraska (NE)' },
    { value: 'NV', label: 'Nevada (NV)' },
    { value: 'NH', label: 'New Hampshire (NH)' },
    { value: 'NJ', label: 'New Jersey (NJ)' },
    { value: 'NM', label: 'New Mexico (NM)' },
    { value: 'NY', label: 'New York (NY)' },
    { value: 'NC', label: 'North Carolina (NC)' },
    { value: 'ND', label: 'North Dakota (ND)' },
    { value: 'OH', label: 'Ohio (OH)' },
    { value: 'OK', label: 'Oklahoma (OK)' },
    { value: 'OR', label: 'Oregon (OR)' },
    { value: 'PA', label: 'Pennsylvania (PA)' },
    { value: 'RI', label: 'Rhode Island (RI)' },
    { value: 'SC', label: 'South Carolina (SC)' },
    { value: 'SD', label: 'South Dakota (SD)' },
    { value: 'TN', label: 'Tennessee (TN)' },
    { value: 'TX', label: 'Texas (TX)' },
    { value: 'UT', label: 'Utah (UT)' },
    { value: 'VT', label: 'Vermont (VT)' },
    { value: 'VA', label: 'Virginia (VA)' },
    { value: 'WA', label: 'Washington (WA)' },
    { value: 'WV', label: 'West Virginia (WV)' },
    { value: 'WI', label: 'Wisconsin (WI)' },
    { value: 'WY', label: 'Wyoming (WY)' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const validateBasicInfo = () => {
    return formData.first_name && formData.last_name && formData.phone_number && 
           formData.business_street && formData.business_city && formData.business_state && formData.business_zip && formData.time_zone;
  };

  const validateCredentialsInfo = () => {
    return formData.credentials.length > 0;
  };

  const validatePhilosophyInfo = () => {
    return formData.training_philosophy.length > 0 && formData.areas_of_expertise.length > 0;
  };

  const validateRatesInfo = () => {
    const first10MinRate = parseFloat(formData.first_10_min_rate);
    const perMinRate = parseFloat(formData.per_minute_rate);
    return formData.experience_years && 
           formData.first_10_min_rate && 
           formData.per_minute_rate && 
           formData.bio &&
           !isNaN(first10MinRate) && first10MinRate >= 10.00 &&
           !isNaN(perMinRate) && perMinRate >= 1.00;
  };

  const validateLegalInfo = () => {
    // Legal states are now optional - no requirement
    return true;
  };

  const validateDocumentsInfo = () => {
    // Documents are optional, so always return true
    return true;
  };

  const handleNext = () => {
    const currentTab = activeTab;
    let nextTab = '';
    
    switch (currentTab) {
      case 'intro':
        nextTab = 'basic';
        setCompletedTabs(prev => new Set([...prev, 'intro']));
        break;
      case 'basic':
        if (validateBasicInfo()) {
          setCompletedTabs(prev => new Set([...prev, 'basic']));
          nextTab = 'credentials';
        }
        break;
      case 'credentials':
        if (validateCredentialsInfo()) {
          setCompletedTabs(prev => new Set([...prev, 'credentials']));
          nextTab = 'philosophy';
        }
        break;
      case 'philosophy':
        if (validatePhilosophyInfo()) {
          setCompletedTabs(prev => new Set([...prev, 'philosophy']));
          nextTab = 'rates';
        }
        break;
      case 'rates':
        if (validateRatesInfo()) {
          setCompletedTabs(prev => new Set([...prev, 'rates']));
          nextTab = 'legal';
        }
        break;
      case 'legal':
        if (validateLegalInfo()) {
          setCompletedTabs(prev => new Set([...prev, 'legal']));
          nextTab = 'documents';
        }
        break;
      case 'documents':
        if (validateDocumentsInfo()) {
          setCompletedTabs(prev => new Set([...prev, 'documents']));
          nextTab = 'background';
        }
        break;
      case 'background':
        if (backgroundForm.consent && backgroundForm.legal_name) {
          setCompletedTabs(prev => new Set([...prev, 'background']));
        }
        break;
    }
    
    if (nextTab) {
      setActiveTab(nextTab);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== SUBMITTING TRAINER SETUP ===');
    console.log('Form data:', formData);
    console.log('validateBasicInfo():', validateBasicInfo());
    console.log('validateCredentialsInfo():', validateCredentialsInfo());
    console.log('validatePhilosophyInfo():', validatePhilosophyInfo());
    console.log('validateRatesInfo():', validateRatesInfo());
    console.log('validateLegalInfo():', validateLegalInfo());
    
    // Check which fields are missing
    if (!validateBasicInfo()) {
      setError('Please complete all Basic Info fields');
      return;
    }
    if (!validateCredentialsInfo()) {
      setError('Please select at least one Credential');
      return;
    }
    if (!validatePhilosophyInfo()) {
      setError('Please select at least one Training Philosophy and Area of Expertise');
      return;
    }
    if (!validateRatesInfo()) {
      setError('Please complete all Experience & Rates fields');
      return;
    }
    // Legal states validation removed - now optional

    try {
      setLoading(true);
      setError(null);

      // Build the full business address
      let fullAddress = formData.business_street;
      if (formData.business_apt) {
        fullAddress += ', ' + formData.business_apt;
      }
      fullAddress += ', ' + formData.business_city + ', ' + formData.business_state + ' ' + formData.business_zip;

      // Create trainer profile
      const response = await apiService.createTrainer({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        address: fullAddress, // Use business address for the address field
        business_name: formData.business_name,
        time_zone: formData.time_zone,
        business_address: fullAddress,
        credentials: formData.credentials,
        training_philosophy: formData.training_philosophy,
        areas_of_expertise: formData.areas_of_expertise,
        experience_years: parseInt(formData.experience_years),
        year_began_training: formData.year_began_training ? parseInt(formData.year_began_training) : null,
        first_10_min_rate: parseFloat(parseFloat(formData.first_10_min_rate).toFixed(2)),
        per_minute_rate: parseFloat(parseFloat(formData.per_minute_rate).toFixed(2)),
        bio: formData.bio,
        legal_states: formData.legal_states
      });
      
      if (response.Code === 200) {
        // Store trainer ID for future use
        localStorage.setItem('trainerId', response.id);
        
        // Upload any pending documents
        const uploadPromises = Object.entries(pendingFiles).map(([docType, file]) =>
          handleFileUpload(docType, file).catch(err => {
            console.error(`Failed to upload ${docType}:`, err);
            return null;
          })
        );
        
        // Wait for all uploads to complete (or fail) before redirecting
        await Promise.all(uploadPromises);
        
        setCompletedTabs(prev => new Set([...prev, 'documents']));
        setBackgroundError('');
        setBackgroundSuccess(false);
        setBackgroundForm(prev => ({
          ...prev,
          legal_name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim()
        }));
        setActiveTab('background');
      } else {
        setError(response.Message || 'Failed to create trainer profile');
      }
    } catch (err) {
      console.error('Failed to create trainer profile:', err);
      setError(err.message || 'Failed to create trainer profile');
    } finally {
      setLoading(false);
    }
  };

  const renderMultiSelect = (field, options, title, required = false) => (
    <div style={styles.field}>
      <label style={styles.label}>
        {title} {required && <span style={styles.required}>*</span>}
      </label>
      <div style={styles.multiSelectContainer}>
        {loadingOptions ? (
          <div style={styles.loading}>Loading options...</div>
        ) : options.length === 0 ? (
          <div style={styles.error}>No options available. Please refresh the page.</div>
        ) : (
          <>
            <div style={styles.optionsGrid}>
              {options.map(option => (
                <div
                  key={option}
                  onClick={() => handleArrayToggle(field, option)}
                  style={{
                    ...styles.optionChip,
                    ...(formData[field].includes(option) ? styles.optionChipSelected : {})
                  }}
                >
                  {option}
                  {formData[field].includes(option) && (
                    <span style={styles.checkmark}>✓</span>
                  )}
                </div>
              ))}
            </div>
            {formData[field].length > 0 && (
              <div style={styles.selectedOptions}>
                <strong>Selected: </strong>
                {formData[field].join(', ')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderIntroTab = () => (
    <div style={styles.introContainer}>
      <div style={styles.introCard}>
        <h2 style={styles.introHeading}>Welcome to the Trainer Network Application</h2>
        <p style={styles.introBody}>
          We’re thrilled you’re here! This guided setup walks you through the information we need to showcase your expertise to potential clients.
        </p>
        <ul style={styles.introList}>
          <li style={styles.introListItem}>
            <span style={styles.introIcon}>📋</span>
            <span>
              <strong>Gather key details.</strong> We’ll cover your business basics, professional experience, training philosophy, and rates.
            </span>
          </li>
          <li style={styles.introListItem}>
            <span style={styles.introIcon}>🧾</span>
            <span>
              <strong>Optional document uploads.</strong> Add resumes, certifications, or degrees to strengthen your profile.
            </span>
          </li>
          <li style={styles.introListItem}>
            <span style={styles.introIcon}>🛡️</span>
            <span>
              <strong>Background check authorization.</strong> We partner with a secure provider and store only your approval status.
            </span>
          </li>
        </ul>
        <div style={styles.introBody}>
          You can revisit any section before submitting. Once everything looks good, we’ll guide you through the background check to complete onboarding.
        </div>
        <div style={styles.introAction}>
          <button
            onClick={handleNext}
            style={styles.button}
          >
            Get Started →
          </button>
        </div>
      </div>
    </div>
  );

  const renderBasicInfoTab = () => (
    <div>
      <h2 style={styles.tabTitle}>Basic Profile Information</h2>
      <form style={styles.form}>
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>
              First Name <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Enter your first name"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>
              Last Name <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Enter your last name"
              required
            />
          </div>
        </div>
        
        <div style={styles.field}>
          <label style={styles.label}>
            Business Name (if applicable)
          </label>
          <input
            type="text"
            name="business_name"
            value={formData.business_name}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="e.g., Pawsitive Training Solutions"
          />
        </div>

        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>
              Email Address <span style={styles.required}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              style={{
                ...styles.input,
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#bdc3c7',
                cursor: 'not-allowed'
              }}
              readOnly
              disabled
            />
            <div style={{
              fontSize: '0.9rem',
              color: '#95a5a6',
              marginTop: '5px',
              fontStyle: 'italic'
            }}>
              Email from your account registration
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>
              Phone Number <span style={styles.required}>*</span>
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="(555) 123-4567"
              required
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            Time Zone <span style={styles.required}>*</span>
          </label>
          <select
            name="time_zone"
            value={formData.time_zone}
            onChange={handleInputChange}
            style={styles.select}
            required
          >
            <option value="" style={styles.option}>Select your time zone...</option>
            {timeZoneOptions.map(zone => (
              <option key={zone} value={zone} style={styles.option}>
                {zone}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            Business Address / Mailing Address <span style={styles.required}>*</span>
          </label>
        </div>
        <div style={styles.field}>
          <label style={styles.sublabel}>Street Address <span style={styles.required}>*</span></label>
          <input
            type="text"
            name="business_street"
            value={formData.business_street}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="123 Main Street"
            required
          />
        </div>
        <div style={styles.field}>
          <label style={styles.sublabel}>Apt, Ste, etc.</label>
          <input
            type="text"
            name="business_apt"
            value={formData.business_apt}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Apt 4B"
          />
        </div>
        <div style={styles.fieldRow}>
          <div style={{...styles.field, flex: 2}}>
            <label style={styles.sublabel}>City <span style={styles.required}>*</span></label>
            <input
              type="text"
              name="business_city"
              value={formData.business_city}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="City"
              required
            />
          </div>
          <div style={{...styles.field, flex: 1, marginRight: '10px'}}>
            <label style={styles.sublabel}>State <span style={styles.required}>*</span></label>
            <input
              type="text"
              name="business_state"
              value={formData.business_state}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="State"
              required
            />
          </div>
          <div style={{...styles.field, flex: 1}}>
            <label style={styles.sublabel}>ZIP Code <span style={styles.required}>*</span></label>
            <input
              type="text"
              name="business_zip"
              value={formData.business_zip}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="ZIP"
              required
            />
          </div>
        </div>
        
        <button
          onClick={handleNext}
          style={{
            ...styles.button,
            ...styles.nextButton,
            ...(!validateBasicInfo() ? styles.buttonDisabled : {})
          }}
          disabled={!validateBasicInfo()}
        >
          Next →
        </button>
      </form>
    </div>
  );

  const renderCredentialsTab = () => (
    <div>
      <h2 style={styles.tabTitle}>Credentials & Certifications</h2>
      <form style={styles.form}>
        {renderMultiSelect('credentials', credentialOptions, 'Credential Filter', true)}
        
        {formData.credentials.includes('Formal degree') && (
          <div style={styles.field}>
            <label style={styles.label}>Formal Degree - Area of Study</label>
            <input
              type="text"
              name="formal_degree"
              value={formData.formal_degree}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="e.g., Animal Behavior, Veterinary Science, Psychology"
            />
          </div>
        )}

        {formData.credentials.includes('Professional Dog Trainer School') && (
          <div style={styles.field}>
            <label style={styles.label}>Professional Dog Trainer School</label>
            <input
              type="text"
              name="professional_school"
              value={formData.professional_school}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="e.g., Karen Pryor Academy, CATCH Canine Trainers Academy"
            />
          </div>
        )}

        {formData.credentials.includes('CCFT – list affiliation') && (
          <div style={styles.field}>
            <label style={styles.label}>CCFT Affiliation</label>
            <input
              type="text"
              name="ccf_affiliation"
              value={formData.ccf_affiliation}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="List your CCFT affiliation"
            />
          </div>
        )}

        {formData.credentials.includes('Other sport judge') && (
          <div style={styles.field}>
            <label style={styles.label}>Other Sport Judge</label>
            <input
              type="text"
              name="other_sport_judge"
              value={formData.other_sport_judge}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="List other sports you judge"
            />
          </div>
        )}

        {formData.credentials.includes('Breed expert') && (
          <div style={styles.field}>
            <label style={styles.label}>Breed Expert</label>
            <input
              type="text"
              name="breed_expert"
              value={formData.breed_expert}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="List breeds you specialize in"
            />
          </div>
        )}

        <div style={styles.field}>
          <label style={styles.label}>Other Credentials</label>
          <textarea
            name="other_credentials"
            value={formData.other_credentials}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="List any other relevant credentials or certifications..."
            rows={3}
          />
        </div>
        
        <button
          onClick={handleNext}
          style={{
            ...styles.button,
            ...styles.nextButton,
            ...(!validateCredentialsInfo() ? styles.buttonDisabled : {})
          }}
          disabled={!validateCredentialsInfo()}
        >
          Next →
        </button>
      </form>
    </div>
  );

  const renderPhilosophyTab = () => (
    <div>
      <h2 style={styles.tabTitle}>Training Philosophy & Methods</h2>
      <form style={styles.form}>
        {renderMultiSelect('training_philosophy', trainingPhilosophyOptions, 'Training Philosophy and Methods', true)}
        {renderMultiSelect('areas_of_expertise', areasOfExpertiseOptions, 'Areas of Expertise and Specializations', true)}
        
        <button
          onClick={handleNext}
          style={{
            ...styles.button,
            ...styles.nextButton,
            ...(!validatePhilosophyInfo() ? styles.buttonDisabled : {})
          }}
          disabled={!validatePhilosophyInfo()}
        >
          Next →
        </button>
      </form>
    </div>
  );

  const renderRatesTab = () => (
    <div>
      <h2 style={styles.tabTitle}>Experience & Rates</h2>
      <form style={styles.form}>
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>
              Years of Experience <span style={styles.required}>*</span>
            </label>
            <input
              type="number"
              name="experience_years"
              value={formData.experience_years}
              onChange={handleInputChange}
              style={styles.input}
              min="0"
              max="50"
              placeholder="5"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>
              Year Began Training Professionally
            </label>
            <input
              type="number"
              name="year_began_training"
              value={formData.year_began_training}
              onChange={handleInputChange}
              style={styles.input}
              min="1950"
              max={new Date().getFullYear()}
              placeholder="2019"
            />
          </div>
        </div>

        <div style={styles.pricingInfo}>
          <div style={styles.pricingTitle}>Session Pricing Structure</div>
          <div style={styles.pricingText}>
            Set your rates for the first 10 minutes and per-minute rate after that.
          </div>
        </div>

        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>
              First 10 Minutes Rate ($) <span style={styles.required}>*</span>
              <span style={{ fontSize: '0.85rem', color: '#94a3b8', marginLeft: '8px' }}>(Minimum: $10.00)</span>
            </label>
            <input
              type="text"
              name="first_10_min_rate"
              value={formData.first_10_min_rate}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string, numbers, and one decimal point
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  handleInputChange({ target: { name: 'first_10_min_rate', value } });
                }
              }}
              onBlur={(e) => {
                // Validate and format on blur
                const value = e.target.value.trim();
                if (value === '') {
                  handleInputChange({ target: { name: 'first_10_min_rate', value: '' } });
                  return;
                }
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  // Enforce minimum of 10.00
                  const validatedValue = Math.max(10.00, numValue);
                  const formatted = parseFloat(validatedValue.toFixed(2));
                  handleInputChange({ target: { name: 'first_10_min_rate', value: formatted.toString() } });
                }
              }}
              style={styles.input}
              placeholder="15.00"
              required
            />
            {formData.first_10_min_rate && parseFloat(formData.first_10_min_rate) < 10 && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '4px' }}>
                Minimum rate is $10.00
              </div>
            )}
          </div>
          <div style={styles.field}>
            <label style={styles.label}>
              Per Minute Rate After 10 Min ($) <span style={styles.required}>*</span>
              <span style={{ fontSize: '0.85rem', color: '#94a3b8', marginLeft: '8px' }}>(Minimum: $1.00)</span>
            </label>
            <input
              type="text"
              name="per_minute_rate"
              value={formData.per_minute_rate}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string, numbers, and one decimal point
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  handleInputChange({ target: { name: 'per_minute_rate', value } });
                }
              }}
              onBlur={(e) => {
                // Validate and format on blur
                const value = e.target.value.trim();
                if (value === '') {
                  handleInputChange({ target: { name: 'per_minute_rate', value: '' } });
                  return;
                }
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  // Enforce minimum of 1.00
                  const validatedValue = Math.max(1.00, numValue);
                  const formatted = parseFloat(validatedValue.toFixed(2));
                  handleInputChange({ target: { name: 'per_minute_rate', value: formatted.toString() } });
                }
              }}
              style={styles.input}
              placeholder="1.00"
              required
            />
            {formData.per_minute_rate && parseFloat(formData.per_minute_rate) < 1 && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '4px' }}>
                Minimum rate is $1.00 per minute
              </div>
            )}
          </div>
        </div>

        <div style={styles.pricingInfo}>
          <div style={styles.pricingTitle}>💡 Pricing Preview</div>
          <div style={styles.pricingText}>
            Your sessions will be displayed as: <strong>${formData.first_10_min_rate || '15.00'} first 10 minutes + ${formData.per_minute_rate || '1.17'}/min after</strong><br/>
            This pricing structure encourages quick, focused sessions while providing fair compensation for longer training sessions.
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            Bio <span style={styles.required}>*</span>
          </label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="Tell clients about your training approach, certifications, experience, and what makes you special as a dog trainer..."
            required
          />
        </div>
        
        <button
          onClick={handleNext}
          style={{
            ...styles.button,
            ...styles.nextButton,
            ...(!validateRatesInfo() ? styles.buttonDisabled : {})
          }}
          disabled={!validateRatesInfo()}
        >
          Next →
        </button>
      </form>
    </div>
  );

  const handleSelectAllStates = () => {
    if (formData.legal_states.length === stateOptions.length) {
      // If all are selected, deselect all
      setFormData(prev => ({
        ...prev,
        legal_states: []
      }));
    } else {
      // Select all states
      setFormData(prev => ({
        ...prev,
        legal_states: stateOptions.map(s => s.value)
      }));
    }
  };

  const renderLegalTab = () => (
    <div>
      <h2 style={styles.tabTitle}>Legal Permissions (Optional)</h2>
      <form style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>
            I am legally permitted to train dogs in the following states (optional):
          </label>
          <div style={{ marginBottom: '15px' }}>
            <button
              type="button"
              onClick={handleSelectAllStates}
              style={{
                ...styles.button,
                padding: '10px 20px',
                fontSize: '0.9rem',
                background: formData.legal_states.length === stateOptions.length 
                  ? '#e74c3c' 
                  : '#3b82f6',
                marginBottom: '15px'
              }}
            >
              {formData.legal_states.length === stateOptions.length 
                ? 'Uncheck All' 
                : 'Check All'}
            </button>
          </div>
          <div style={styles.multiSelectContainer}>
            <div style={styles.optionsGrid}>
              {stateOptions.map(state => (
                <div
                  key={state.value}
                  onClick={() => handleArrayToggle('legal_states', state.value)}
                  style={{
                    ...styles.optionChip,
                    ...(formData.legal_states.includes(state.value) ? styles.optionChipSelected : {})
                  }}
                >
                  {state.label}
                  {formData.legal_states.includes(state.value) && (
                    <span style={styles.checkmark}>✓</span>
                  )}
                </div>
              ))}
            </div>
            {formData.legal_states.length > 0 && (
              <div style={styles.selectedOptions}>
                <strong>Selected States: </strong>
                {formData.legal_states.map(state => 
                  stateOptions.find(s => s.value === state)?.label
                ).join(', ')}
              </div>
            )}
          </div>
        </div>
        
        <button
          onClick={handleNext}
          style={{
            ...styles.button,
            ...styles.nextButton
          }}
        >
          Next →
        </button>
      </form>
    </div>
  );

  const handleFileSelect = (documentType, file) => {
    if (!file) return;
    // Store file for upload after profile creation
    setPendingFiles(prev => ({
      ...prev,
      [documentType]: file
    }));
    setUploadedDocuments(prev => ({
      ...prev,
      [documentType]: { filename: file.name, pending: true }
    }));
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;
    
    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) {
      setError('Please complete trainer setup first');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.uploadTrainerDocument(trainerId, documentType, file);
      if (response.Code === 200) {
        setUploadedDocuments(prev => ({
          ...prev,
          [documentType]: { filename: response.filename, file_path: response.file_path }
        }));
        setPendingFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[documentType];
          return newFiles;
        });
        setError(null);
      } else {
        setError(response.Message || 'Failed to upload document');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleFileDelete = async (documentType) => {
    if (!window.confirm(`Are you sure you want to delete this ${documentType}?`)) {
      return;
    }

    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) {
      setError('Trainer ID not found');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteTrainerDocument(trainerId, documentType);
      if (response.Code === 200) {
        setUploadedDocuments(prev => ({
          ...prev,
          [documentType]: null
        }));
        setError(null);
      } else {
        setError(response.Message || 'Failed to delete document');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentField = (documentType, label, accept, multiple = false) => {
    const uploadedFile = uploadedDocuments[documentType];
    
    return (
      <div style={styles.field}>
        <label style={styles.label}>{label}</label>
        {uploadedFile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
            <span style={{ color: uploadedFile.pending ? '#f39c12' : '#2ecc71' }}>
              {uploadedFile.pending ? '⏳' : '✓'} {uploadedFile.filename}
              {uploadedFile.pending && ' (will upload after profile creation)'}
            </span>
            <button
              type="button"
              onClick={() => {
                if (uploadedFile.pending) {
                  setUploadedDocuments(prev => ({
                    ...prev,
                    [documentType]: null
                  }));
                  setPendingFiles(prev => {
                    const newFiles = { ...prev };
                    delete newFiles[documentType];
                    return newFiles;
                  });
                } else {
                  handleFileDelete(documentType);
                }
              }}
              style={{
                padding: '5px 10px',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              {uploadedFile.pending ? 'Remove' : 'Delete'}
            </button>
          </div>
        ) : (
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            style={styles.fileInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileSelect(documentType, file);
              }
            }}
          />
        )}
      </div>
    );
  };

  const renderDocumentsTab = () => (
    <div>
      <h2 style={styles.tabTitle}>Document Upload</h2>
      <form style={styles.form}>
        <div style={styles.pricingInfo}>
          <div style={styles.pricingTitle}>Document Upload (Optional)</div>
          <div style={styles.pricingText}>
            Upload your resume, certificates, degrees, and insurance certificate. 
            These documents will be stored securely and can be updated later. All uploads are optional.
            You can delete and re-upload files if needed.
          </div>
        </div>

        {renderDocumentField('resume', 'Resume', '.pdf,.doc,.docx')}
        {renderDocumentField('certificates', 'Certificates', '.pdf,.doc,.docx,.jpg,.jpeg,.png', true)}
        {renderDocumentField('degrees', 'Degrees', '.pdf,.doc,.docx,.jpg,.jpeg,.png', true)}
        {renderDocumentField('insurance', 'Insurance Certificate', '.pdf,.doc,.docx,.jpg,.jpeg,.png')}
        
        <button
          onClick={handleSubmit}
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {})
          }}
          disabled={loading}
        >
          {loading ? 'Saving Profile...' : 'Continue to Background Check'}
        </button>
      </form>
    </div>
  );

  const handleBackgroundSubmit = async (e) => {
    e.preventDefault();
    setBackgroundError('');
    setBackgroundSuccess(false);

    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) {
      setBackgroundError('We could not locate your trainer profile. Please save your profile information first.');
      return;
    }

    if (!backgroundForm.legal_name.trim()) {
      setBackgroundError('Please provide your full legal name.');
      return;
    }

    if (!backgroundForm.date_of_birth) {
      setBackgroundError('Please provide your date of birth.');
      return;
    }

    if (!backgroundForm.consent) {
      setBackgroundError('You must authorize the background check to continue.');
      return;
    }

    try {
      setBackgroundSubmitting(true);
      await apiService.submitBackgroundCheck({
        trainer_id: trainerId,
        legal_name: backgroundForm.legal_name.trim(),
        date_of_birth: backgroundForm.date_of_birth,
        consent: true
      });

      setBackgroundSuccess(true);
      setCompletedTabs(prev => new Set([...prev, 'background']));

      // Redirect to payment
      try {
        const checkoutResponse = await apiService.createTrainerRegistrationCheckout({ trainer_id: trainerId });
        
        if (checkoutResponse.Code === 200) {
          if (checkoutResponse.url) {
            // Redirect to Stripe Payment Link
            window.location.href = checkoutResponse.url;
          } else {
            setBackgroundError('Failed to get payment link. Please try again.');
            setBackgroundSubmitting(false);
          }
        } else {
          setBackgroundError(checkoutResponse.Message || 'Failed to initiate payment. Please try again.');
          setBackgroundSubmitting(false);
        }
      } catch (paymentErr) {
        console.error('Payment initiation failed:', paymentErr);
        setBackgroundError('Failed to initiate payment. Please try again.');
        setBackgroundSubmitting(false);
      }
    } catch (err) {
      console.error('Background check submission failed:', err);
      setBackgroundError(err.message || 'Failed to submit background check. Please try again.');
      setBackgroundSubmitting(false);
    }
  };

  const renderBackgroundCheckTab = () => {
    const trainerId = localStorage.getItem('trainerId');

    return (
      <div>
        <h2 style={styles.tabTitle}>Background Check & Final Review</h2>
        <div style={styles.backgroundInfoBox}>
          <div style={styles.backgroundInfoTitle}>Why this matters</div>
          <p style={styles.backgroundInfoText}>
            Completing your background check is the final step toward joining the Pheefee trainer network.
            We use a third-party provider and do <strong>not</strong> store any of your sensitive background check data.
            Only the approval status is retained in your profile.
          </p>
          <ul style={styles.backgroundInfoList}>
            <li>Review the information below carefully before submitting.</li>
            <li>Your submission authorizes Pheefee to run a background check for platform eligibility.</li>
            <li>We will email you once your screening is complete.</li>
          </ul>
        </div>

        {!trainerId && (
          <div style={styles.error}>
            We couldn't locate your trainer profile. Please save your profile information first.
          </div>
        )}

        <form style={styles.form} onSubmit={handleBackgroundSubmit}>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>
                Legal Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="legal_name"
                value={backgroundForm.legal_name}
                onChange={(e) =>
                  setBackgroundForm(prev => ({ ...prev, legal_name: e.target.value }))
                }
                style={styles.input}
                placeholder="Enter your full legal name"
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>
                Date of Birth <span style={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={backgroundForm.date_of_birth}
                onChange={(e) =>
                  setBackgroundForm(prev => ({ ...prev, date_of_birth: e.target.value }))
                }
                style={styles.input}
                required
              />
            </div>
          </div>

          <div style={styles.backgroundConsentBox}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={backgroundForm.consent}
                onChange={(e) =>
                  setBackgroundForm(prev => ({ ...prev, consent: e.target.checked }))
                }
                style={styles.checkbox}
              />
              <span>
                I authorize Pheefee to run a background check using the information above. I understand that Pheefee
                will not store my background check details—only the approval status needed for my trainer profile.
              </span>
            </label>
          </div>

          {backgroundError && <div style={styles.error}>{backgroundError}</div>}
          {backgroundSuccess && (
            <div style={styles.success}>
              Background check submitted! We’ll notify you once review is complete.
            </div>
          )}

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(backgroundSubmitting ? styles.buttonDisabled : {})
            }}
            disabled={backgroundSubmitting || !trainerId}
          >
            {backgroundSubmitting ? 'Processing...' : 'Pay and Submit'}
          </button>
        </form>
      </div>
    );
  };

  const renderCurrentTab = () => {
    switch (activeTab) {
      case 'intro':
        return renderIntroTab();
      case 'basic':
        return renderBasicInfoTab();
      case 'credentials':
        return renderCredentialsTab();
      case 'philosophy':
        return renderPhilosophyTab();
      case 'rates':
        return renderRatesTab();
      case 'legal':
        return renderLegalTab();
      case 'documents':
        return renderDocumentsTab();
      case 'background':
        return renderBackgroundCheckTab();
      default:
        return renderBasicInfoTab();
    }
  };

  const styles = {
    container: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: '#0f172a',
      minHeight: '100vh',
      color: '#f1f5f9',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: '#1e293b',
      color: '#f1f5f9',
      padding: '20px 0',
      boxShadow: '0 1px 0 rgba(0, 0, 0, 0.2)',
      borderBottom: '1px solid #334155'
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    noticeBanner: {
      background: 'rgba(56, 189, 248, 0.1)',
      border: '1px solid rgba(56, 189, 248, 0.35)',
      borderRadius: '12px',
      padding: '20px 24px',
      marginBottom: '30px',
      color: '#e0f2fe',
      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.3)'
    },
    noticeTitle: {
      fontSize: '1.4rem',
      fontWeight: '700',
      marginBottom: '8px',
      color: '#38bdf8'
    },
    noticeText: {
      fontSize: '1rem',
      lineHeight: '1.6',
      color: '#bae6fd'
    },
    introContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    introCard: {
      background: 'rgba(15, 23, 42, 0.65)',
      border: '1px solid rgba(148, 163, 184, 0.25)',
      borderRadius: '16px',
      padding: '28px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      boxShadow: '0 18px 40px rgba(15, 23, 42, 0.45)'
    },
    introHeading: {
      fontSize: '1.8rem',
      fontWeight: '700',
      color: '#f8fafc',
      letterSpacing: '-0.01em'
    },
    introBody: {
      color: '#cbd5f5',
      fontSize: '1rem',
      lineHeight: '1.7'
    },
    introList: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    introListItem: {
      display: 'flex',
      gap: '14px',
      color: '#e2e8f0',
      lineHeight: '1.6',
      fontSize: '1rem'
    },
    introIcon: {
      fontSize: '1.6rem'
    },
    introAction: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '12px'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      cursor: 'pointer'
    },
    logoIcon: {
      width: '40px',
      height: '40px',
      background: '#3b82f6',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1.2rem'
    },
    logoText: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#f1f5f9'
    },
    backButton: {
      background: 'transparent',
      border: '1px solid #475569',
      color: '#cbd5e1',
      padding: '10px 20px',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'all 0.15s ease'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      padding: '20px'
    },
    topTabs: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '32px',
      overflowX: 'auto',
      paddingBottom: '8px',
      gap: '12px'
    },
    topTabButton: {
      padding: '14px 24px',
      borderRadius: '10px',
      border: 'none',
      background: '#334155',
      color: '#94a3b8',
      fontSize: '0.9375rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      whiteSpace: 'nowrap',
      position: 'relative',
      minWidth: '140px',
      justifyContent: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    topTabButtonActive: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      fontWeight: '600',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
      transform: 'translateY(-2px)'
    },
    topTabButtonCompleted: {
      background: '#334155',
      color: '#22c55e',
      border: '2px solid #22c55e',
      fontWeight: '600'
    },
    tabConnector: {
      flex: 1,
      height: '3px',
      background: '#475569',
      borderRadius: '2px',
      position: 'relative'
    },
    tabConnectorCompleted: {
      background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
    },
    sidebar: {
      width: '300px',
      background: '#1e293b',
      borderRadius: '12px',
      padding: '20px',
      marginRight: '20px',
      border: '1px solid #334155',
      height: 'fit-content',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
    },
    tabButton: {
      width: '100%',
      padding: '12px 18px',
      marginBottom: '8px',
      background: 'transparent',
      border: '1px solid #475569',
      borderRadius: '8px',
      color: '#94a3b8',
      fontSize: '0.9375rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      position: 'relative'
    },
    tabButtonActive: {
      background: '#334155',
      border: '1px solid #3b82f6',
      color: '#3b82f6',
      fontWeight: '600',
      transform: 'translateX(3px)'
    },
    tabButtonCompleted: {
      background: 'rgba(34, 197, 94, 0.1)',
      border: '1px solid #22c55e',
      color: '#22c55e'
    },
    tabIcon: {
      fontSize: '1.2rem'
    },
    checkmark: {
      position: 'absolute',
      right: '15px',
      fontSize: '1rem',
      fontWeight: 'bold'
    },
    contentArea: {
      flex: 1,
      background: '#1e293b',
      borderRadius: '12px',
      padding: '30px',
      border: '1px solid #334155',
      overflowY: 'auto',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
      minHeight: '500px'
    },
    tabTitle: {
      fontSize: '1.75rem',
      fontWeight: '600',
      marginBottom: '24px',
      color: '#f1f5f9',
      letterSpacing: '-0.01em'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '0.9375rem',
      fontWeight: '600',
      color: '#cbd5e1',
      letterSpacing: '-0.01em'
    },
    sublabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#94a3b8',
      marginBottom: '8px',
      display: 'block'
    },
    fieldRow: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    },
    input: {
      padding: '12px 14px',
      borderRadius: '8px',
      border: '1px solid #334155',
      background: '#334155',
      color: '#f1f5f9',
      fontSize: '0.9375rem',
      transition: 'all 0.15s ease'
    },
    textarea: {
      padding: '12px 14px',
      borderRadius: '8px',
      border: '1px solid #334155',
      background: '#334155',
      color: '#f1f5f9',
      fontSize: '0.9375rem',
      minHeight: '120px',
      resize: 'vertical',
      fontFamily: 'inherit',
      transition: 'all 0.15s ease'
    },
    select: {
      padding: '12px 14px',
      borderRadius: '8px',
      border: '1px solid #334155',
      background: '#334155',
      color: '#f1f5f9',
      fontSize: '0.9375rem',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 15px center',
      backgroundSize: '16px',
      paddingRight: '45px'
    },
    fileInput: {
      padding: '15px',
      borderRadius: '10px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '1rem',
      cursor: 'pointer'
    },
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '0.9375rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      background: '#3b82f6',
      color: 'white',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
      marginTop: '20px',
      alignSelf: 'flex-end'
    },
    nextButton: {
      alignSelf: 'flex-end',
      marginTop: '30px'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    error: {
      background: 'rgba(231, 76, 60, 0.2)',
      border: '1px solid #e74c3c',
      color: '#e74c3c',
      padding: '15px',
      borderRadius: '10px',
      marginTop: '20px',
      textAlign: 'center'
    },
    loading: {
      textAlign: 'center',
      color: '#f39c12',
      fontSize: '1.1rem',
      marginTop: '20px'
    },
    pricingInfo: {
      background: 'rgba(243, 156, 18, 0.1)',
      border: '1px solid rgba(243, 156, 18, 0.3)',
      borderRadius: '10px',
      padding: '20px',
      marginBottom: '20px'
    },
    pricingTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#f39c12',
      marginBottom: '10px'
    },
    pricingText: {
      color: '#ecf0f1',
      lineHeight: '1.6'
    },
    backgroundInfoBox: {
      background: 'rgba(56, 189, 248, 0.1)',
      border: '1px solid rgba(56, 189, 248, 0.25)',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      color: '#e0f2fe'
    },
    backgroundInfoTitle: {
      fontSize: '1.3rem',
      fontWeight: '600',
      color: '#38bdf8',
      marginBottom: '10px'
    },
    backgroundInfoText: {
      fontSize: '1rem',
      lineHeight: '1.6',
      color: '#bae6fd',
      marginBottom: '10px'
    },
    backgroundInfoList: {
      paddingLeft: '20px',
      color: '#bae6fd',
      lineHeight: '1.6'
    },
    backgroundConsentBox: {
      background: 'rgba(34, 197, 94, 0.12)',
      border: '1px solid rgba(34, 197, 94, 0.3)',
      borderRadius: '12px',
      padding: '18px',
      marginTop: '20px',
      color: '#bbf7d0'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      fontSize: '1rem',
      lineHeight: '1.6',
      cursor: 'pointer'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      marginTop: '4px'
    },
    success: {
      background: 'rgba(16, 185, 129, 0.18)',
      border: '1px solid rgba(16, 185, 129, 0.5)',
      color: '#34d399',
      padding: '15px',
      borderRadius: '10px',
      marginTop: '20px',
      textAlign: 'center'
    },
    required: {
      color: '#e74c3c'
    },
    multiSelectContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '10px',
      maxHeight: '300px',
      overflowY: 'auto',
      padding: '10px',
      background: '#334155',
      borderRadius: '8px',
      border: '1px solid #475569'
    },
    optionChip: {
      padding: '10px 15px',
      background: '#475569',
      border: '1px solid #64748b',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#cbd5e1'
    },
    optionChipSelected: {
      background: '#3b82f6',
      border: '1px solid #3b82f6',
      color: 'white',
      fontWeight: '600'
    },
    selectedOptions: {
      padding: '10px 15px',
      background: 'rgba(46, 204, 113, 0.1)',
      border: '1px solid rgba(46, 204, 113, 0.3)',
      borderRadius: '8px',
      color: '#2ecc71',
      fontSize: '0.9rem'
    },
    option: {
      background: '#2c3e50',
      color: 'white',
      padding: '10px'
    }
  };

  const tabs = [
    { id: 'intro', label: 'Welcome', icon: '👋', short: 'Intro' },
    { id: 'basic', label: 'Basic Info', icon: '', short: 'Basic' },
    { id: 'credentials', label: 'Credentials', icon: '', short: 'Credentials' },
    { id: 'philosophy', label: 'Philosophy', icon: '', short: 'Philosophy' },
    { id: 'rates', label: 'Rates & Bio', icon: '', short: 'Rates' },
    { id: 'legal', label: 'Legal States', icon: '', short: 'Legal' },
    { id: 'documents', label: 'Documents', icon: '', short: 'Docs' },
    { id: 'background', label: 'Background Check', icon: '', short: 'Background' }
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo} onClick={onBackToHome}>
            <img 
              src="/images/logo.png" 
              alt="Pheefee Logo" 
              style={{...styles.logoIcon, width: '40px', height: '40px', objectFit: 'contain'}}
            />
            <span style={styles.logoText}>Pheefee</span>
          </div>
          <button
            onClick={onBackToHome}
            style={styles.backButton}
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div style={styles.mainContent}>
        <div style={styles.noticeBanner}>
          <div style={styles.noticeTitle}>Complete Every Step</div>
          <div style={styles.noticeText}>
            To be considered for the Pheefee trainer network you must finish <strong>all</strong> sections,
            including document review and the background check. We’ll guide you through each requirement—stay the course!
          </div>
        </div>

        {/* Top Navigation Tabs */}
        <div style={styles.topTabs}>
          {tabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              {index > 0 && (
                <div
                  style={{
                    ...styles.tabConnector,
                    ...(completedTabs.has(tabs[index - 1].id) ? styles.tabConnectorCompleted : {})
                  }}
                />
              )}
              <button
                onClick={() => handleTabChange(tab.id)}
                style={{
                  ...styles.topTabButton,
                  ...(activeTab === tab.id ? styles.topTabButtonActive : {}),
                  ...(completedTabs.has(tab.id) ? styles.topTabButtonCompleted : {})
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
                <span>{activeTab === tab.id ? tab.label : tab.short}</span>
                {completedTabs.has(tab.id) && <span style={{ marginLeft: '8px', fontSize: '1.1rem' }}>✓</span>}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          {renderCurrentTab()}

          {error && <div style={styles.error}>{error}</div>}
          {loading && <div style={styles.loading}>Setting up your trainer profile...</div>}
        </div>
      </div>
    </div>
  );
};

export default TrainerSetup;